import React, { useState, useEffect } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { ArrowBack, WhatsApp } from "@mui/icons-material";
import { authService } from "../../services/authService";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [showSupport, setShowSupport] = useState(false);

  // Redirigir al login después de 1 minuto cuando se muestre el mensaje de éxito
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        navigate("/login");
      }, 60000); // 1 minuto = 60000 ms

      return () => clearTimeout(timer);
    }
  }, [success, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    if (showSupport) {
      setLoading(false);
      return;
    }

    try {
      // 1. Verificar si el correo existe en BD
      const checkResult = await authService.checkEmail(email);

      if (!checkResult.exists) {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);

        if (newAttempts >= 3) {
          setShowSupport(true);
          setError("Has superado el límite de intentos. Contacta a soporte.");
        } else {
          setError(`El correo no está registrado. Intento ${newAttempts}/3`);
        }
        setLoading(false);
        return;
      }

      // 2. Si existe, enviar correo vía Resend
      await authService.resetPassword(email);
      setSuccess(
        "Se ha enviado un correo con las instrucciones para restablecer tu contraseña.",
      );
    } catch (err) {
      console.error(err);
      // Mantener mensaje genérico para otros errores de red/sistema
      setError("Ocurrió un error al procesar tu solicitud. Intenta más tarde.");
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppClick = () => {
    window.open("https://wa.me/525610979946", "_blank");
    setShowSupport(false); // Opcional: cerrar el modal tras click
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)",
        padding: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            p: 4,
            background: "rgba(19, 24, 41, 0.9)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: 3,
          }}
        >
          <Box sx={{ mb: 4 }}>
            <Link
              component={RouterLink}
              to="/login"
              sx={{
                display: "flex",
                alignItems: "center",
                color: "text.secondary",
                mb: 2,
                textDecoration: "none",
                "&:hover": { color: "white" },
              }}
            >
              <ArrowBack sx={{ fontSize: 18, mr: 1 }} /> Volver al Login
            </Link>
            <Typography
              variant="h5"
              component="h1"
              gutterBottom
              sx={{ fontWeight: 700, color: "white" }}
            >
              Recuperar Contraseña
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ingresa tu correo electrónico y te enviaremos las instrucciones.
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Dialog open={showSupport} onClose={() => {}}>
              <DialogTitle>⚠️ Atención Requerida</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Has excedido el número máximo de intentos o tu correo no se
                  encuentra en sistema.
                  <br />
                  <br />
                  Por favor, ponte en contacto con soporte técnico para
                  restablecer tu acceso manualmente.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleWhatsAppClick}
                  variant="contained"
                  color="success"
                  startIcon={<WhatsApp />}
                  autoFocus
                >
                  Contactar por WhatsApp
                </Button>
              </DialogActions>
            </Dialog>

            <TextField
              fullWidth
              label="Correo Electrónico"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              disabled={loading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  color: "white",
                  "& fieldset": { borderColor: "rgba(255, 255, 255, 0.23)" },
                  "&:hover fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.5)",
                  },
                },
                "& .MuiInputLabel-root": { color: "rgba(255, 255, 255, 0.7)" },
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? "Enviando..." : "Enviar Instrucciones"}
            </Button>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default ForgotPassword;
